/**
 * Instant Quote Wizard — instant-quote.js
 * Place in: assets/instant-quote.js
 * Pairs with: snippets/instant-quote.liquid
 *
 * Multi-step quote builder: lead details → products & quantities →
 * branding → deadline → "building your quote" animation → instant
 * price breakdown → submits via a hidden native Shopify contact form.
 *
 * Pricing rules (kept in sync manually with the live theme — same
 * approach as bulk-order-widget.js's tier table):
 *   - Quantity tiers match the "Massy Custom Pricing and Volume
 *     Discount" app config: 1-9 (0%), 10-24 (5%), 25-49 (7%),
 *     50-74 (10%), 75-99 (12%), 100+ (14%). Applied per garment
 *     line (its own total quantity across sizes), not combined
 *     across different products — this matches how Massy actually
 *     discounts at checkout, so the estimate never overpromises.
 *   - Branding: the single logo/text placement this form collects
 *     is included free — matches the "Logo Setup Free — No Setup
 *     Fees" messaging used across the industry pages and the
 *     branding-wizard's own free-first-position rule.
 *
 * Submission: Shopify's native /contact endpoint now rejects a
 * background fetch() POST with "Missing CAPTCHA token" — Shopify's
 * own spam protection only attaches to a genuine browser form
 * submission. So the final step fills the hidden {% form 'contact' %}
 * rendered by instant-quote.liquid and does a real (non-AJAX) submit.
 * Shopify redirects back to this same page afterward; the built quote
 * is cached in sessionStorage beforehand and restored into the same
 * "Quote Sent" confirmation screen on reload, using the
 * `#iq-form-posted` marker instant-quote.liquid renders when
 * `form.posted_successfully?` is true.
 */

(function () {
  'use strict';

  const IQ_TIERS = [
    { min: 1, max: 9, save: 0 },
    { min: 10, max: 24, save: 5 },
    { min: 25, max: 49, save: 7 },
    { min: 50, max: 74, save: 10 },
    { min: 75, max: 99, save: 12 },
    { min: 100, max: 99999, save: 14 }
  ];

  const CLOUDINARY_CLOUD_NAME = 'ctqdjy7j';
  const CLOUDINARY_UPLOAD_PRESET = 'Rosewear Logos';
  const SESSION_KEY = 'iqQuoteState';

  const root = document.getElementById('iq-root');
  if (!root) return;

  const state = {
    step: 1,
    lead: { company: '', contactName: '', email: '', phone: '', industry: '', estQty: '' },
    garments: [], // { id, handle, title, image, price, hasSize, hasColour, sizeOptionIndex, colourOptionIndex, variants, sizes, colours, selectedColour, quantities }
    branding: { method: 'Embroidery', position: 'Left Chest', logoUrl: '', logoName: '' },
    deadline: '',
    sent: false
  };

  let garmentSeq = 0;

  // ── DOM refs ─────────────────────────────────────────────
  const trackerSteps = root.querySelectorAll('[data-tracker-step]');
  const panels = root.querySelectorAll('[data-panel]');
  const searchInput = document.getElementById('iq-product-search');
  const searchResultsEl = document.getElementById('iq-search-results');
  const garmentsEl = document.getElementById('iq-garments');
  const emptyNote = document.getElementById('iq-empty-note');
  const step2Continue = document.getElementById('iq-step2-continue');
  const methodCards = root.querySelectorAll('.iq-method-card');
  const positionSelect = document.getElementById('iq-position');
  const uploadZone = document.getElementById('iq-upload-zone');
  const logoInput = document.getElementById('iq-logo-input');
  const uploadPrompt = document.getElementById('iq-upload-prompt');
  const uploadFileEl = document.getElementById('iq-upload-file');
  const uploadFilenameEl = document.getElementById('iq-upload-filename');
  const deadlineInput = document.getElementById('iq-deadline');
  const buildBtn = document.getElementById('iq-build-btn');
  const sendBtn = document.getElementById('iq-send-btn');
  const resultTotalEl = document.getElementById('iq-result-total');
  const breakdownEl = document.getElementById('iq-breakdown');
  const sentStateEl = document.getElementById('iq-sent-state');
  const resultMainEl = document.getElementById('iq-result-main');

  // Hidden native contact form (see snippets/instant-quote.liquid)
  const hiddenContactForm = document.querySelector('#iq-contact-form-wrap form');
  const hfName = document.getElementById('iq-hidden-name');
  const hfEmail = document.getElementById('iq-hidden-email');
  const hfPhone = document.getElementById('iq-hidden-phone');
  const hfBody = document.getElementById('iq-hidden-body');
  const hfCompany = document.getElementById('iq-hidden-company');
  const hfIndustry = document.getElementById('iq-hidden-industry');
  const hfRequiredBy = document.getElementById('iq-hidden-required-by');
  const hfTotal = document.getElementById('iq-hidden-total');
  const hfLogoUrl = document.getElementById('iq-hidden-logo-url');

  // Shopify's own hCaptcha bootstrap (the "captcha-bootstrap" inline
  // script Shopify injects site-wide) only "binds" hCaptcha to a
  // protected form — i.e. loads the hCaptcha script and lets it run its
  // (usually invisible) risk check — when a shopper naturally interacts
  // with one of that form's fields (a real focusin/change event). This
  // form's hidden fields are filled programmatically just before submit,
  // which never fires those events, so the bootstrap script's own submit
  // handler prevents the default submit and waits forever for a bind
  // that never happens — this, not display:none, is the actual cause of
  // the "stuck on Sending…" bug: confirmed live by watching Shopify's
  // own preventDefault() call in the debugger. Firing a synthetic
  // 'change' event on a hidden field here, as early as page load, kicks
  // off that binding immediately, so hCaptcha has the wizard's full
  // duration (well over a minute for a real shopper filling in four
  // steps) to complete its check quietly in the background, long before
  // they ever reach "Email Me This Quote".
  if (hiddenContactForm) {
    const hcaptchaPrimeField = document.getElementById('iq-hidden-email');
    if (hcaptchaPrimeField) {
      hcaptchaPrimeField.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Deadline: no earlier than 5 working-days-ish out
  if (deadlineInput) {
    const min = new Date();
    min.setDate(min.getDate() + 7);
    deadlineInput.min = min.toISOString().slice(0, 10);
  }

  // ── Helpers ──────────────────────────────────────────────
  function formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  // /search/suggest.json returns product prices already formatted in
  // major currency units (e.g. "35.25"), NOT pence — unlike /products/
  // {handle}.js, which returns pence like the rest of this file expects.
  // Using formatMoney() (÷100) on a suggest.json price was showing
  // "£0.35" for a £35.25 product. Format it directly instead.
  function formatSuggestPrice(price) {
    if (price === null || price === undefined || price === '') return '';
    const n = parseFloat(price);
    if (isNaN(n)) return '';
    return '£' + n.toFixed(2);
  }

  function getTier(totalQty) {
    if (totalQty < 1) return IQ_TIERS[0];
    return IQ_TIERS.find((t) => totalQty >= t.min && totalQty <= t.max) || IQ_TIERS[0];
  }

  function garmentQtyTotal(garment) {
    return Object.values(garment.quantities).reduce((a, b) => a + b, 0);
  }

  function garmentLineTotal(garment) {
    const qty = garmentQtyTotal(garment);
    const tier = getTier(qty);
    const unitPrice = Math.round(garment.price * (100 - tier.save) / 100);
    return { qty, tier, unitPrice, lineTotal: unitPrice * qty };
  }

  function quoteGrandTotal() {
    return state.garments.reduce((sum, g) => sum + garmentLineTotal(g).lineTotal, 0);
  }

  // ── Step navigation ──────────────────────────────────────
  function goToStep(step) {
    state.step = step;
    panels.forEach((p) => {
      p.classList.toggle('iq-panel-active', p.dataset.panel === String(step));
    });
    trackerSteps.forEach((el) => {
      const n = parseInt(el.dataset.trackerStep, 10);
      const target = typeof step === 'number' ? step : 5;
      el.classList.toggle('iq-active', n === target);
      el.classList.toggle('iq-done', n < target);
    });
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function validateStep1() {
    const fields = [
      ['iq-company', state.lead.company !== ''],
      ['iq-contact-name', state.lead.contactName !== ''],
      ['iq-email', /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.lead.email)],
      ['iq-phone', state.lead.phone !== ''],
      ['iq-industry', state.lead.industry !== ''],
      ['iq-est-qty', state.lead.estQty !== '' && parseInt(state.lead.estQty, 10) > 0]
    ];
    let valid = true;
    fields.forEach(([id, ok]) => {
      const field = document.getElementById(id).closest('.iq-field');
      field.classList.toggle('iq-field-error', !ok);
      if (!ok) valid = false;
    });
    return valid;
  }

  function validateStep4() {
    const field = deadlineInput.closest('.iq-field');
    const ok = state.deadline !== '';
    field.classList.toggle('iq-field-error', !ok);
    return ok;
  }

  root.addEventListener('input', () => {
    state.lead.company = document.getElementById('iq-company').value.trim();
    state.lead.contactName = document.getElementById('iq-contact-name').value.trim();
    state.lead.email = document.getElementById('iq-email').value.trim();
    state.lead.phone = document.getElementById('iq-phone').value.trim();
    state.lead.industry = document.getElementById('iq-industry').value;
    state.lead.estQty = document.getElementById('iq-est-qty').value;
    state.deadline = deadlineInput.value;
  });

  root.querySelectorAll('[data-iq-next]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = parseInt(btn.dataset.iqNext, 10);
      if (state.step === 1 && !validateStep1()) return;
      if (state.step === 2 && state.garments.length === 0) return;
      goToStep(target);
    });
  });

  root.querySelectorAll('[data-iq-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      goToStep(parseInt(btn.dataset.iqBack, 10));
    });
  });

  // ── Step 2: product search ───────────────────────────────
  let searchDebounce;
  let searchAbort;

  searchInput.addEventListener('input', () => {
    const q = searchInput.value.trim();
    clearTimeout(searchDebounce);
    if (q.length < 2) {
      searchResultsEl.classList.remove('iq-open');
      searchResultsEl.innerHTML = '';
      return;
    }
    searchDebounce = setTimeout(() => runSearch(q), 280);
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.iq-search-wrap')) {
      searchResultsEl.classList.remove('iq-open');
    }
  });

  function runSearch(q) {
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();

    fetch(
      '/search/suggest.json?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide',
      { signal: searchAbort.signal }
    )
      .then((res) => res.json())
      .then((data) => {
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        renderSearchResults(products);
      })
      .catch(() => {});
  }

  function renderSearchResults(products) {
    searchResultsEl.innerHTML = '';
    if (products.length === 0) {
      searchResultsEl.innerHTML = '<div class="iq-search-empty">No Products Found — Try A Different Search</div>';
      searchResultsEl.classList.add('iq-open');
      return;
    }
    products.forEach((p) => {
      const row = document.createElement('div');
      row.className = 'iq-search-result';
      const img = p.featured_image ? p.featured_image.url : '';
      row.innerHTML =
        '<img src="' + img + '" alt="" loading="lazy">' +
        '<div><div class="iq-search-result__title">' + p.title + '</div>' +
        '<div class="iq-search-result__price">' + formatSuggestPrice(p.price) + '</div></div>';
      row.addEventListener('click', () => {
        addGarment(p.handle);
        searchInput.value = '';
        searchResultsEl.classList.remove('iq-open');
      });
      searchResultsEl.appendChild(row);
    });
    searchResultsEl.classList.add('iq-open');
  }

  function addGarment(handle) {
    if (state.garments.some((g) => g.handle === handle)) return;

    fetch('/products/' + handle + '.js')
      .then((res) => res.json())
      .then((product) => {
        let sizeIdx = -1;
        let colourIdx = -1;
        product.options.forEach((opt, i) => {
          const optName = typeof opt === 'string' ? opt : (opt && opt.name) || '';
          const n = optName.toLowerCase();
          if (n === 'size') sizeIdx = i;
          if (n === 'color' || n === 'colour') colourIdx = i;
        });

        const sizes = sizeIdx > -1 ? [...new Set(product.variants.map((v) => v.options[sizeIdx]))] : [];
        const colours = colourIdx > -1 ? [...new Set(product.variants.map((v) => v.options[colourIdx]))] : [];

        // Map each colour to its own variant image (falling back to the
        // product's featured image) so the thumbnail can update when the
        // shopper picks a different "Preferred Colour".
        const fallbackImage = product.featured_image || (product.images[0] || '');
        const colourImageMap = {};
        if (colourIdx > -1) {
          colours.forEach((c) => {
            const match = product.variants.find((v) => v.options[colourIdx] === c && v.featured_image);
            colourImageMap[c] = match ? match.featured_image.src : fallbackImage;
          });
        }

        const initialColour = colours[0] || null;

        const garment = {
          id: 'g' + (garmentSeq++),
          handle: product.handle,
          title: product.title,
          image: (initialColour && colourImageMap[initialColour]) || fallbackImage,
          price: product.variants[0] ? product.variants[0].price : 0,
          sizeOptionIndex: sizeIdx,
          colourOptionIndex: colourIdx,
          variants: product.variants,
          sizes: sizes,
          colours: colours,
          colourImageMap: colourImageMap,
          selectedColour: initialColour,
          quantities: {}
        };

        state.garments.push(garment);
        renderGarments();
        updateStep2Continue();
      })
      .catch(() => {});
  }

  function removeGarment(id) {
    state.garments = state.garments.filter((g) => g.id !== id);
    renderGarments();
    updateStep2Continue();
  }

  function updateStep2Continue() {
    const hasQty = state.garments.some((g) => garmentQtyTotal(g) > 0);
    step2Continue.disabled = !hasQty;
    emptyNote.style.display = state.garments.length === 0 ? 'block' : 'none';
  }

  function renderGarments() {
    garmentsEl.innerHTML = '';
    state.garments.forEach((g) => {
      const card = document.createElement('div');
      card.className = 'iq-garment';
      card.dataset.garmentId = g.id;

      const totals = garmentLineTotal(g);

      let colourHtml = '';
      if (g.colours.length) {
        colourHtml =
          '<div class="iq-garment__colour"><label>Preferred Colour</label><br><select data-role="colour">' +
          g.colours.map((c) => '<option value="' + c + '"' + (c === g.selectedColour ? ' selected' : '') + '>' + c + '</option>').join('') +
          '</select></div>';
      }

      let sizeHtml = '';
      if (g.sizes.length) {
        sizeHtml = g.sizes
          .map((size) => {
            const qty = g.quantities[size] || 0;
            return (
              '<div class="iq-qtybox" data-size="' + size + '">' +
              '<span class="iq-qtybox__label">' + size + '</span>' +
              '<button type="button" class="iq-qtybox__btn" data-qty-action="minus">−</button>' +
              '<span class="iq-qtybox__val">' + qty + '</span>' +
              '<button type="button" class="iq-qtybox__btn" data-qty-action="plus">+</button>' +
              '</div>'
            );
          })
          .join('');
      } else {
        const qty = g.quantities['default'] || 0;
        sizeHtml =
          '<div class="iq-qtybox" data-size="default">' +
          '<span class="iq-qtybox__label">Qty</span>' +
          '<button type="button" class="iq-qtybox__btn" data-qty-action="minus">−</button>' +
          '<span class="iq-qtybox__val">' + qty + '</span>' +
          '<button type="button" class="iq-qtybox__btn" data-qty-action="plus">+</button>' +
          '</div>';
      }

      card.innerHTML =
        '<div class="iq-garment__head">' +
        (g.image ? '<img src="' + g.image + '" alt="">' : '') +
        '<span class="iq-garment__title">' + g.title + '<br><span class="iq-garment__price">' + formatMoney(g.price) + ' Each (Before Discount)</span></span>' +
        '<button type="button" class="iq-garment__remove" aria-label="Remove">×</button>' +
        '</div>' +
        '<div class="iq-garment__options">' + sizeHtml + colourHtml + '</div>' +
        '<div class="iq-garment__total">' +
        (totals.qty > 0
          ? 'Qty ' + totals.qty + ' × ' + formatMoney(totals.unitPrice) + (totals.tier.save > 0 ? ' (' + totals.tier.save + '% Bulk Discount Applied)' : '') + ' = <strong>' + formatMoney(totals.lineTotal) + '</strong>'
          : 'Set A Quantity Above To See Pricing') +
        '</div>';

      card.querySelector('.iq-garment__remove').addEventListener('click', () => removeGarment(g.id));

      const colourSelect = card.querySelector('[data-role="colour"]');
      if (colourSelect) {
        colourSelect.addEventListener('change', (e) => {
          g.selectedColour = e.target.value;
          if (g.colourImageMap && g.colourImageMap[g.selectedColour]) {
            g.image = g.colourImageMap[g.selectedColour];
          }
          renderGarments();
        });
      }

      card.querySelectorAll('.iq-qtybox').forEach((box) => {
        const size = box.dataset.size;
        box.querySelectorAll('.iq-qtybox__btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const current = g.quantities[size] || 0;
            const next = btn.dataset.qtyAction === 'plus' ? current + 1 : Math.max(0, current - 1);
            g.quantities[size] = next;
            renderGarments();
            updateStep2Continue();
          });
        });
      });

      garmentsEl.appendChild(card);
    });
    updateStep2Continue();
  }

  // ── Step 3: branding ─────────────────────────────────────
  methodCards.forEach((card) => {
    card.addEventListener('click', () => {
      methodCards.forEach((c) => c.classList.remove('iq-selected'));
      card.classList.add('iq-selected');
      state.branding.method = card.dataset.method;
    });
  });
  if (methodCards.length) methodCards[0].classList.add('iq-selected');

  positionSelect.addEventListener('change', () => {
    state.branding.position = positionSelect.value;
  });
  state.branding.position = positionSelect.value;

  uploadZone.addEventListener('click', () => logoInput.click());
  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.classList.add('iq-dragover');
  });
  uploadZone.addEventListener('dragleave', () => uploadZone.classList.remove('iq-dragover'));
  uploadZone.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadZone.classList.remove('iq-dragover');
    if (e.dataTransfer.files[0]) handleLogoFile(e.dataTransfer.files[0]);
  });
  logoInput.addEventListener('change', () => {
    if (logoInput.files[0]) handleLogoFile(logoInput.files[0]);
  });

  function handleLogoFile(file) {
    state.branding.logoName = file.name;
    uploadPrompt.style.display = 'none';
    uploadFileEl.classList.add('iq-visible');
    uploadFilenameEl.textContent = 'Uploading ' + file.name + '…';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    fetch('https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD_NAME + '/auto/upload', {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.secure_url) {
          state.branding.logoUrl = data.secure_url;
          uploadFilenameEl.textContent = file.name + ' — Uploaded';
        } else {
          uploadFilenameEl.textContent = file.name + ' — Saved (We\'ll Confirm By Email)';
        }
      })
      .catch(() => {
        uploadFilenameEl.textContent = file.name + ' — Saved (We\'ll Confirm By Email)';
      });
  }

  // ── Build & result ────────────────────────────────────────
  buildBtn.addEventListener('click', () => {
    if (!validateStep4()) return;
    goToStep('building');
    runBuildAnimation();
  });

  function runBuildAnimation() {
    const items = document.querySelectorAll('#iq-building-list .iq-building__item');
    items.forEach((el) => el.classList.remove('iq-done'));
    let i = 0;
    function step() {
      if (i >= items.length) {
        setTimeout(showResult, 400);
        return;
      }
      items[i].classList.add('iq-done');
      i++;
      setTimeout(step, 620);
    }
    setTimeout(step, 350);
  }

  function showResult() {
    const total = quoteGrandTotal();
    resultTotalEl.textContent = formatMoney(total);

    breakdownEl.innerHTML = '';
    state.garments.forEach((g) => {
      const t = garmentLineTotal(g);
      const row = document.createElement('div');
      row.className = 'iq-breakdown__row';
      row.innerHTML =
        '<div><div class="iq-breakdown__row-name">' + g.title + '</div>' +
        '<div class="iq-breakdown__row-meta">Qty ' + t.qty + ' × ' + formatMoney(t.unitPrice) +
        (t.tier.save > 0 ? ' (' + t.tier.save + '% Off)' : '') + '</div></div>' +
        '<div class="iq-breakdown__row-price">' + formatMoney(t.lineTotal) + '</div>';
      breakdownEl.appendChild(row);
    });

    const brandingRow = document.createElement('div');
    brandingRow.className = 'iq-breakdown__row iq-breakdown__row-branding';
    brandingRow.innerHTML =
      '<div>Branding — ' + state.branding.method + ', ' + state.branding.position + '</div><div>Included</div>';
    breakdownEl.appendChild(brandingRow);

    goToStep('result');
  }

  sendBtn.addEventListener('click', () => {
    if (state.sent) return;
    state.sent = true;
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending…';
    submitQuoteViaHiddenForm();
  });

  function buildQuoteBody() {
    const lines = [];
    lines.push('=== Instant Quote — Rosewear ===');
    lines.push('');
    lines.push('Company: ' + state.lead.company);
    lines.push('Quote Reference: ' + (state.quoteReference || ''));
    lines.push('Contact: ' + state.lead.contactName);
    lines.push('Industry: ' + state.lead.industry);
    lines.push('Estimated Quantity (As First Indicated): ' + state.lead.estQty);
    lines.push('Required By: ' + state.deadline);
    lines.push('');
    lines.push('--- Products ---');
    state.garments.forEach((g) => {
      const t = garmentLineTotal(g);
      lines.push(
        g.title + ' — Colour: ' + (g.selectedColour || 'N/A') + ' — Qty ' + t.qty + ' @ ' + formatMoney(t.unitPrice) +
          (t.tier.save > 0 ? ' (' + t.tier.save + '% Bulk Discount)' : '') + ' = ' + formatMoney(t.lineTotal)
      );
    });
    lines.push('');
    lines.push('--- Branding ---');
    lines.push('Method: ' + state.branding.method);
    lines.push('Placement: ' + state.branding.position);
    lines.push('Logo: ' + (state.branding.logoUrl ? state.branding.logoUrl : (state.branding.logoName ? state.branding.logoName + ' (upload not confirmed — customer may email separately)' : 'Not Uploaded — Customer May Send Separately')));
    lines.push('');
    lines.push('--- Instant Quote Total ---');
    lines.push(formatMoney(quoteGrandTotal()) + ' (Estimate — Excludes VAT & Delivery)');
    return lines.join('\n');
  }

  // Persists the built quote so it can be restored after the hidden
  // form's real submit navigates away and Shopify redirects back here.
  function saveQuoteToSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        lead: state.lead,
        garments: state.garments,
        branding: state.branding,
        deadline: state.deadline
      }));
    } catch (e) {}
  }

  // ── Klaviyo: "Quote Requested" event ────────────────────
  // Fired the moment a quote is built (same point the hidden contact
  // form is submitted). Klaviyo's public key is safe to expose
  // client-side — the klaviyo.js snippet loaded in theme.liquid
  // exposes `klaviyo` globally. A Klaviyo Flow triggered on this
  // metric sends the branded confirmation email; its "Confirm This
  // Order" button links to /pages/confirm-quote?ref=...&data=... using
  // quote_reference and quote_data_base64 below.
  function generateQuoteReference() {
    const d = new Date();
    const datePart = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    const randPart = Math.floor(100 + Math.random() * 900);
    return 'RW-' + datePart + '-' + randPart;
  }

  function buildKlaviyoProducts() {
    return state.garments.map((g) => {
      const t = garmentLineTotal(g);
      return {
        product_name: g.title,
        colour: g.selectedColour || '',
        quantity: t.qty,
        unit_price: formatMoney(t.unitPrice),
        line_total: formatMoney(t.lineTotal)
      };
    });
  }

  function buildKlaviyoBranding() {
    if (!state.branding || !state.branding.method) return [];
    return [{
      method: state.branding.method,
      placement: state.branding.position,
      type: 'Logo'
    }];
  }

  // btoa() only handles Latin1 — wrap with encodeURIComponent so any
  // UTF-8 characters (accented names, etc.) survive the round trip;
  // the confirm-quote page's decoder reverses this exact scheme.
  function b64EncodeUnicode(str) {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (match, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  }

  function trackQuoteRequestedInKlaviyo() {
    try {
      const ref = generateQuoteReference();
      state.quoteReference = ref;
      const quoteData = {
        company_name: state.lead.company,
        quote_reference: ref,
        contact_name: state.lead.contactName,
        contact_email: state.lead.email,
        contact_phone: state.lead.phone,
        industry: state.lead.industry,
        required_by: state.deadline,
        quote_total: formatMoney(quoteGrandTotal()),
        products: buildKlaviyoProducts(),
        branding_items: buildKlaviyoBranding()
      };
      const quoteDataBase64 = b64EncodeUnicode(JSON.stringify(quoteData));

      if (typeof klaviyo === 'undefined') return;
      klaviyo.identify({
        email: state.lead.email,
        first_name: state.lead.contactName,
        phone_number: state.lead.phone
      });
      klaviyo.push(['track', 'Quote Requested', Object.assign({}, quoteData, { quote_data_base64: quoteDataBase64 })]);
    } catch (e) {}
  }

  function submitQuoteViaHiddenForm() {
    trackQuoteRequestedInKlaviyo();
    saveQuoteToSession();

    if (hfName) hfName.value = state.lead.contactName + ' (' + state.lead.company + ')';
    if (hfEmail) hfEmail.value = state.lead.email;
    if (hfPhone) hfPhone.value = state.lead.phone;
    if (hfBody) hfBody.value = buildQuoteBody();
    if (hfCompany) hfCompany.value = state.lead.company;
    if (hfIndustry) hfIndustry.value = state.lead.industry;
    if (hfRequiredBy) hfRequiredBy.value = state.deadline;
    if (hfTotal) hfTotal.value = formatMoney(quoteGrandTotal());
    if (hfLogoUrl) hfLogoUrl.value = state.branding.logoUrl || '';

    if (hiddenContactForm) {
      // IMPORTANT: form.submit() does NOT fire a "submit" event (a
      // long-standing DOM quirk) — Shopify's own anti-spam script relies
      // on that event to attach the CAPTCHA token, so a plain .submit()
      // call here still gets rejected with "Missing CAPTCHA token" even
      // though it's a real (non-fetch) navigation. requestSubmit() fires
      // the full submission lifecycle (submit event included) exactly
      // like a genuine button click, so Shopify's script gets to run.
      // Belt-and-braces: re-fire the same priming event in case the
      // page-load dispatch above somehow didn't take (e.g. this snippet
      // rendered after that code ran). Shopify's bootstrap script marks
      // a form as processed the first time it runs, so calling this
      // again is a harmless no-op once already bound.
      const hcaptchaPrimeField = document.getElementById('iq-hidden-email');
      if (hcaptchaPrimeField) {
        hcaptchaPrimeField.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (hiddenContactForm.requestSubmit) {
        hiddenContactForm.requestSubmit();
      } else {
        hiddenContactForm.submit();
      }

      // Safety net: if Shopify's anti-spam script (or anything else)
      // intercepts the submit and never actually navigates — which is
      // exactly what happens if the form's container is ever
      // display:none, since reCAPTCHA/challenge widgets refuse to
      // initialise inside one — don't strand the shopper on "Sending…"
      // forever. If we're still on this page a few seconds later, fall
      // back to the confirmation screen; their details are still saved
      // in sessionStorage and the emailed copy simply won't have gone
      // out, so surface a note prompting them to call/email directly.
      setTimeout(() => {
        if (document.body.contains(sendBtn) && sendBtn.textContent === 'Sending…') {
          resultMainEl.style.display = 'none';
          sentStateEl.classList.add('iq-visible');
          const textEl = sentStateEl.querySelector('.iq-sent-state__text');
          if (textEl) {
            textEl.textContent = 'We Saved Your Quote, But Could Not Confirm The Email Sent From This Browser. Please Call Us On The Number Above Or Email Us Your Details So We Can Follow Up.';
          }
        }
      }, 8000);
    } else {
      // Fallback: hidden form missing for some reason — at least don't
      // strand the user on "Sending…" forever.
      resultMainEl.style.display = 'none';
      sentStateEl.classList.add('iq-visible');
    }
  }

  // ── Restore after Shopify redirects back post-submit ─────
  function restoreFromSessionIfPosted() {
    const posted = document.getElementById('iq-form-posted');
    if (!posted) return;
    let saved;
    try {
      saved = sessionStorage.getItem(SESSION_KEY);
    } catch (e) {
      saved = null;
    }
    if (!saved) return;
    let data;
    try {
      data = JSON.parse(saved);
    } catch (e) {
      return;
    }
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (e) {}

    state.lead = data.lead || state.lead;
    state.garments = data.garments || [];
    state.branding = data.branding || state.branding;
    state.deadline = data.deadline || state.deadline;
    state.sent = true;

    showResult();
    resultMainEl.style.display = 'none';
    sentStateEl.classList.add('iq-visible');
  }

  restoreFromSessionIfPosted();
})();
